import React from "react";
import { motion } from "framer-motion";
import { PROOF_METRICS, PROOF_FILMS, TESTIMONIALS } from "../data/content";

/* ═══════════════════════════════════════════════════════════════
   PROOF — The section that answers "why should I trust you"
   Sits immediately after the Hero. Numbers first · films second ·
   producer quotes third.
   ═══════════════════════════════════════════════════════════════ */
export default function Proof() {
  return (
    <section
      id="proof"
      className="relative border-t border-[var(--vw-border)] bg-[var(--vw-bg-soft)]"
      data-testid="vw-proof"
    >
      {/* ── Metric strip (numbers first) ─────────────────────────── */}
      <div className="vw-container pt-16 md:pt-24 pb-14 md:pb-16">
        <div className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="vw-eyebrow mb-4">THE RECEIPT · 2013 — 2026</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.92] tracking-tight">
              Not a promise. <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">A ledger.</span>
            </h2>
          </div>
          <p className="max-w-sm text-[var(--vw-text-dim)] leading-relaxed text-[15px]">
            Numbers we'll stand behind on the phone, on paper, and in a room with your line producer.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 bg-[var(--vw-border)] gap-px border border-[var(--vw-border)]">
          {PROOF_METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="p-6 md:p-8 relative bg-[var(--vw-bg)]"
              data-testid={`vw-proof-metric-${i}`}
            >
              <div className="font-display text-4xl md:text-5xl lg:text-6xl leading-none tracking-tighter text-[var(--vw-text)] mb-3">
                {m.value}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-cyan)] mb-2">
                {m.label}
              </div>
              <div className="text-[12px] text-[var(--vw-text-mute)] leading-relaxed">
                {m.note}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Films we've helped build (posters) ───────────────────── */}
      <div className="vw-container pb-16 md:pb-24">
        <div className="mb-10 md:mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="vw-eyebrow mb-3">FILMS WE'VE HELPED BUILD</div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-tight max-w-[20ch]">
              Delivered to streamers, broadcasters, and theatrical windows on <span className="font-serif-italic font-normal">four continents</span>.
            </h3>
          </div>
          <p className="text-[13px] font-mono uppercase tracking-[0.22em] text-[var(--vw-text-mute)] md:text-right">
            Posters and titles<br/>redacted where NDAs apply.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {PROOF_FILMS.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 6) * 0.06 }}
              className="group relative aspect-[3/4] border border-[var(--vw-border)] bg-[var(--vw-surface)] overflow-hidden hover:border-[var(--vw-cyan)] transition-colors"
              data-testid={`vw-proof-film-${i}`}
            >
              {/* Placeholder poster — swap with real key art */}
              {f.poster ? (
                <img
                  src={f.poster}
                  alt={f.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  {/* Ghost poster grid — evokes a film strip without pretending to be one */}
                  <svg viewBox="0 0 100 133" className="w-full h-full opacity-[0.08]" aria-hidden>
                    <defs>
                      <linearGradient id={`gp${i}`} x1="0" y1="0" x2="100" y2="133" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#00E5FF"/>
                        <stop offset="100%" stopColor="#2B6CFF"/>
                      </linearGradient>
                    </defs>
                    <rect x="6" y="6" width="88" height="121" fill="none" stroke={`url(#gp${i})`} strokeWidth="0.6"/>
                    <path d={`M6 ${20 + (i * 6) % 40} L94 ${60 + (i * 4) % 30}`} stroke={`url(#gp${i})`} strokeWidth="0.4"/>
                    <path d={`M6 ${80 + (i * 3) % 20} L94 ${40 + (i * 5) % 40}`} stroke={`url(#gp${i})`} strokeWidth="0.4"/>
                    <circle cx="50" cy={70 + (i * 2) % 20} r="18" fill="none" stroke={`url(#gp${i})`} strokeWidth="0.4"/>
                  </svg>
                </div>
              )}

              {/* Top badge */}
              <div className="absolute top-2.5 left-2.5 right-2.5 flex items-center justify-between">
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--vw-cyan)] bg-black/50 px-1.5 py-0.5 border border-[var(--vw-cyan)]/40">
                  {f.title}
                </span>
                {f.nda && (
                  <span className="font-mono text-[8.5px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] bg-black/60 px-1.5 py-0.5">
                    NDA
                  </span>
                )}
              </div>

              {/* Bottom meta */}
              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black via-black/70 to-transparent">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[var(--vw-text)] mb-0.5">
                  {f.meta}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
                  {f.territory}
                </div>
              </div>

              {/* Redacted stamp for NDA titles */}
              {f.nda && !f.poster && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="rotate-[-8deg] border border-[var(--vw-text-mute)]/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]/70">
                    Covered by NDA
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] flex items-center gap-3">
          <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
          Full credit list available under mutual NDA · Ask for the deck
        </p>
      </div>

      {/* ── Producer testimonials ────────────────────────────────── */}
      <div className="vw-container border-t border-[var(--vw-border)] py-16 md:py-24">
        <div className="mb-10 md:mb-12">
          <div className="vw-eyebrow mb-3">FROM THE PEOPLE WHO SIGN THE CHECKS</div>
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-tight max-w-[24ch]">
            What producers say <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">off the record</span>.
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="border border-[var(--vw-border)] bg-[var(--vw-bg)] p-7 md:p-8 flex flex-col justify-between min-h-[220px]"
              data-testid={`vw-testimonial-${i}`}
            >
              <svg width="22" height="16" viewBox="0 0 22 16" fill="none" className="mb-5 text-[var(--vw-cyan)]" aria-hidden>
                <path d="M0 16V8.727C0 3.909 3.09 0 8.222 0v3.273C5.6 3.273 4 5.09 4 7.636h4.222V16H0zM13.778 16V8.727C13.778 3.909 16.867 0 22 0v3.273c-2.622 0-4.222 1.818-4.222 4.363H22V16h-8.222z" fill="currentColor"/>
              </svg>
              <blockquote className="font-serif-italic text-xl md:text-[22px] leading-[1.35] text-[var(--vw-text)] mb-6">
                {t.quote}
              </blockquote>
              <figcaption className="border-t border-[var(--vw-border)] pt-4 flex items-center justify-between gap-3">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--vw-text)]">
                    {t.name}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] mt-1">
                    {t.title}
                  </div>
                </div>
                {t.nda && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] border border-[var(--vw-border-strong)] px-1.5 py-0.5 shrink-0">
                    Named under NDA
                  </span>
                )}
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a href="/case-study/2m-feature" className="vw-btn-primary vw-focus" data-testid="vw-proof-cta">
            Read the $1.8M Feature Case Study
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </a>
          <a href="#portfolio" className="vw-btn-ghost vw-focus" data-testid="vw-proof-cta-secondary">
            See the Full Portfolio
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </a>
        </div>
      </div>
    </section>
  );
}
